import type { ApiClient } from './ApiClient';
import type { ClassLevel } from './class';

// Forme alignée sur GET /dashboard/financial-overview (DSH-01,
// apps/api/src/routes/dashboard.ts) — seule source de vérité pour cette
// route. Montants en CHAÎNE (jamais `number`), même prudence que tous les
// autres montants monétaires de ce projet. `recoveryRate` reste un
// `number|null` (fraction 0-1, pas un pourcentage) — calcul déjà fait côté
// serveur (Financial Aggregation Service), le frontend se contente de
// multiplier par 100 pour l'affichage.
export type FinancialHealthStatus = 'NEUTRAL' | 'GOOD' | 'WARNING' | 'CRITICAL';

// DSH-02 : les 5 valeurs RÉELLES de `FeeType` + `SUPPLIES` (SupplyOrder,
// catégorie distincte) — voir apps/api/src/lib/financialAggregation.ts pour
// le détail complet (seule source de vérité pour cette forme).
export type FinancialCategory = 'TUITION' | 'REGISTRATION' | 'CANTEEN' | 'TRANSPORT' | 'OTHER' | 'SUPPLIES';

export interface FinancialCategoryBreakdown {
  category: FinancialCategory;
  // UX-20 : brut PAR catégorie — voir apps/api/src/lib/financialAggregation.ts
  // pour le détail (absent avant ce ticket, seulement agrégé globalement).
  grossInvoiced: string;
  invoiced: string;
  collected: string;
}

export interface FinancialOverview {
  schoolYearLabel: string | null;
  // PAY-10 : distinction BRUT/NET — `totalInvoiced` reste le total NET
  // (INCHANGÉ). `totalGrossInvoiced` = avant réduction, `totalDiscountApplied`
  // = différence, DÉRIVÉE côté serveur (jamais recalculée ici).
  totalGrossInvoiced: string;
  totalDiscountApplied: string;
  totalInvoiced: string;
  totalCollected: string;
  // ⚠️ UX-20 : impayé AU SENS LARGE (inclut les échéances futures pas encore
  // dues) — DISTINCT de `totalOverdueAmount` ci-dessous (réellement en
  // retard). Voir apps/api/src/lib/financialAggregation.ts pour le détail
  // complet de cette distinction. N'est plus affiché par FinancialDashboard.tsx
  // depuis ce ticket, gardé côté type pour un usage futur éventuel.
  totalOutstanding: string;
  // UX-20 : NOUVEAU — somme des soldes des SEULES factures OVERDUE.
  totalOverdueAmount: string;
  recoveryRate: number | null;
  healthStatus: FinancialHealthStatus;
  overdueInvoiceCount: number;
  averageOverdueDays: number | null;
  categoryBreakdown: FinancialCategoryBreakdown[];
}

export interface RecentFinancialActivityEntry {
  id: string;
  kind: 'INVOICE_PAYMENT' | 'SUPPLY_ORDER_PAYMENT';
  amount: string;
  method: string;
  status: string;
  occurredAt: string;
  studentFirstName: string;
  studentLastName: string;
}

// UX-20 : barre "Scolarité — {mois en cours}" — voir
// apps/api/src/lib/financialAggregation.ts#computeCurrentMonthTuitionSummary
// pour le détail (INDÉPENDANT de `schoolYearId`, toujours "le mois calendaire
// en cours").
export interface CurrentMonthTuitionSummary {
  month: string;
  invoiced: string;
  collected: string;
  studentCount: number;
}

// UX-20 : graphique "Encaissements mensuels" — voir
// apps/api/src/lib/financialAggregation.ts#computeMonthlyCollectionSeries
// pour le détail (MÊME `schoolYearId` que `overview`).
export interface MonthlyCollectionEntry {
  month: string;
  collected: string;
}

export interface MonthlyCollectionSeries {
  schoolYearLabel: string | null;
  months: MonthlyCollectionEntry[];
}

export interface FinancialOverviewResponse {
  overview: FinancialOverview;
  recentActivity: RecentFinancialActivityEntry[];
  currentMonthTuition: CurrentMonthTuitionSummary;
  monthlyCollections: MonthlyCollectionSeries;
}

export function getFinancialOverview(
  client: ApiClient,
  accessToken: string,
  schoolYearId?: string,
): Promise<FinancialOverviewResponse> {
  const query = schoolYearId ? `?schoolYearId=${encodeURIComponent(schoolYearId)}` : '';
  return client.get<FinancialOverviewResponse>(`/dashboard/financial-overview${query}`, { accessToken });
}

// ─── DSH-04 : vue financière par classe et par élève (drill-down) ─────────
// Formes alignées sur GET /dashboard/financial-overview/by-class(/students)
// (apps/api/src/routes/dashboard.ts) — même Financial Aggregation Service
// que ci-dessus, jamais un second calcul dupliqué côté serveur.

export interface ClassFinancialSummary {
  // `null` == panier "Non affecté" (élèves sans classe assignée cette
  // année) — INCLUS dans la liste, jamais filtré silencieusement.
  classId: string | null;
  className: string | null;
  level: ClassLevel | null;
  studentCount: number;
  totalGrossInvoiced: string;
  totalInvoiced: string;
  totalCollected: string;
  totalOutstanding: string;
  recoveryRate: number | null;
  healthStatus: FinancialHealthStatus;
}

export interface FinancialOverviewByClassResponse {
  schoolYearLabel: string | null;
  classes: ClassFinancialSummary[];
}

export interface StudentFinancialSummary {
  studentId: string;
  studentFirstName: string;
  studentLastName: string;
  classId: string | null;
  className: string | null;
  level: ClassLevel | null;
  totalGrossInvoiced: string;
  totalInvoiced: string;
  totalCollected: string;
  totalOutstanding: string;
}

export interface FinancialOverviewByStudentResponse {
  schoolYearLabel: string | null;
  classId: string | null;
  className: string | null;
  level: ClassLevel | null;
  students: StudentFinancialSummary[];
}

// Sentinelle littérale pour le panier "Non affecté" — même valeur EXACTE
// que `UNASSIGNED_CLASS_SENTINEL` côté serveur (apps/api/src/routes/dashboard.ts),
// à garder synchronisée manuellement (aucun package partagé entre les deux
// couches au-delà de ce client HTTP).
export const UNASSIGNED_CLASS_ID = 'unassigned';

export function getFinancialOverviewByClass(
  client: ApiClient,
  accessToken: string,
  schoolYearId?: string,
): Promise<FinancialOverviewByClassResponse> {
  const query = schoolYearId ? `?schoolYearId=${encodeURIComponent(schoolYearId)}` : '';
  return client.get<FinancialOverviewByClassResponse>(`/dashboard/financial-overview/by-class${query}`, { accessToken });
}

export function getFinancialOverviewByClassStudents(
  client: ApiClient,
  classId: string,
  accessToken: string,
  schoolYearId?: string,
): Promise<FinancialOverviewByStudentResponse> {
  const params = new URLSearchParams({ classId });
  if (schoolYearId) params.set('schoolYearId', schoolYearId);
  return client.get<FinancialOverviewByStudentResponse>(`/dashboard/financial-overview/by-class/students?${params.toString()}`, { accessToken });
}

// DSH-05 — branche le bouton "Export comptable (CSV)" déjà en place (UX-20,
// `FinancialDashboard.tsx`). Renvoie le CSV BRUT (texte) — même pattern EXACT
// que `exportOverdueInvoicesCsv()` (DSH-03, `overdueInvoices.ts`) :
// `ApiClient` renvoie déjà le corps tel quel pour une réponse non-JSON.
export function exportFinancialTransactionsCsv(client: ApiClient, accessToken: string, schoolYearId?: string): Promise<string> {
  const query = schoolYearId ? `?schoolYearId=${encodeURIComponent(schoolYearId)}` : '';
  return client.get<string>(`/dashboard/financial-overview/export${query}`, { accessToken });
}
